import Auth from '@aws-amplify/auth';

interface Token {
  getJwtToken: () => string;
  getExpiration: () => number;
  decodePayload?: () => { [key: string]: any };
}
interface Session {
  getIdToken: () => Token;
  getAccessToken: () => Token;
  getRefreshToken: () => {
    getToken: () => string;
    getExpiration?: () => number;
  };
}

export const MAX_SESSION_LENGTH = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

// Stored-token branch parity: callers (e.g. AccountSubMenu) rely on decodePayload().iat.
const decodeJwtPayload = (jwt: string): { [key: string]: any } => {
  try {
    return JSON.parse(atob(jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return {};
  }
};

// Cognito SAML provider for Microsoft Entra - must match the name registered in each env's user pool.
const entraEnv = (process.env.REACT_APP_ENVIRONMENT || 'dev').toLowerCase();
export const ENTRA_IDP_NAME = `tpn-ms-entra-${entraEnv}`;

export async function signInWithEntra() {
  return Auth.federatedSignIn({ customProvider: ENTRA_IDP_NAME });
}

export default async function getAuthSession(): Promise<Session> {
  const storedIdToken = localStorage.getItem('amplifyIdToken');
  const storedAccessToken = localStorage.getItem('amplifyAccessToken');
  const storedExpiry = localStorage.getItem('amplifyTokenExpiry');
  const expiresAt = storedExpiry ? parseInt(storedExpiry, 10) : 0;

  //  If tokens exist & are still valid, return full session object
  if (storedIdToken && storedAccessToken && storedExpiry && Date.now() < expiresAt) {
    return {
      getIdToken: () => ({
        getJwtToken: () => storedIdToken,
        getExpiration: () => expiresAt / 1000,
        decodePayload: () => decodeJwtPayload(storedIdToken),
      }),
      getAccessToken: () => ({
        getJwtToken: () => storedAccessToken,
        getExpiration: () => expiresAt / 1000,
        decodePayload: () => decodeJwtPayload(storedAccessToken),
      }),
      getRefreshToken: () => ({
        getToken: () => localStorage.getItem('amplifyRefreshToken') || '',
      }),
    };
  }

  //  Fetch new session from Cognito if expired or missing
  const session = await Auth.currentSession();
  const idToken = session.getIdToken();
  const accessToken = session.getAccessToken();
  const refreshToken = session.getRefreshToken();

  //  Store the new ID token & access token separately
  localStorage.setItem('amplifyIdToken', idToken.getJwtToken());
  localStorage.setItem('amplifyAccessToken', accessToken.getJwtToken());
  localStorage.setItem('amplifyTokenExpiry', (accessToken.getExpiration() * 1000).toString());

  return {
    getIdToken: () => idToken,
    getAccessToken: () => accessToken,
    getRefreshToken: () => refreshToken,
  };
}

// Persist session-validation state after sign-in from the cached session (no forced bypassCache refresh, which 400s on a stale refresh token and previously threw -> double sign-in, TPE-2850). Never throws.
export async function persistSignInSession(): Promise<void> {
  try {
    const session = await Auth.currentSession();

    const idToken = session.getIdToken();
    const accessToken = session.getAccessToken();
    const refreshToken = session.getRefreshToken();
    const expiresAt = accessToken.getExpiration() * 1000; // Convert to ms

    localStorage.setItem('amplifyIdToken', idToken.getJwtToken());
    localStorage.setItem('amplifyAccessToken', accessToken.getJwtToken());
    localStorage.setItem('amplifyRefreshToken', refreshToken.getToken());
    localStorage.setItem('amplifyTokenExpiry', expiresAt.toString());
  } catch (error) {
    console.error('Error persisting sign-in session:', error);
    // Session read failed: drop the stale token keys so getAuthSession can't serve dead JWTs (it falls through to Auth.currentSession()), and set a bounded expiry so validateSession doesn't log the fresh sign-in out.
    localStorage.removeItem('amplifyIdToken');
    localStorage.removeItem('amplifyAccessToken');
    localStorage.removeItem('amplifyRefreshToken');
    localStorage.setItem('amplifyTokenExpiry', (Date.now() + MAX_SESSION_LENGTH).toString());
  } finally {
    localStorage.setItem('fixedLoginTime', Date.now().toString());
  }
}
